import type { CollectionColorEnum } from '@assembly-web/services';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  Content,
  Root as CollapsibleRoot,
  Trigger,
} from '@radix-ui/react-collapsible';
import type { MouseEventHandler, ReactNode, RefObject } from 'react';
import { useState } from 'react';
import ContentLoader from 'react-content-loader';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import {
  Toolbar,
  type ToolbarItem,
} from '../../../DesignSystem/Feedback/Toolbar';
import { classNames } from '../../../DesignSystem/Utils/classNames';
import { mapColorToCssClass } from '../../Shared/EmojiColorPicker/utils';
import { checkCursorInsideElement } from './utils';

type URLTarget = { url: string };
type OnClickTarget = { onClick: () => void };

type CardProps = {
  children?: ReactNode;
  icon: ReactNode;
  text: string | TrustedHTML;
  subText?: string | TrustedHTML;
  connectedRight?: ReactNode;
  primaryToolbarItems?: ToolbarItem[];
  secondaryToolbarItems?: ToolbarItem[];
  onToolbarItemClick?: (args: ToolbarItem) => void;
  preview?: ReactNode;
  wrapperRef?: RefObject<HTMLAnchorElement>;
  detailsRef?: RefObject<HTMLDivElement>;
  onMenuToggle?: (isOpen: boolean) => void;
  collapsibleContent?: ReactNode;
  color?: CollectionColorEnum;
  target?: string;
  IS_ACTIVE?: boolean;
  wrapperClassName?: string;
} & Partial<OnClickTarget> &
  Partial<URLTarget>;

type WrapperProps = {
  isActive: boolean;
  isOpen: boolean;
  children: ReactNode;
  className?: string;
  onMouseEnter: () => void;
  onMouseLeave: MouseEventHandler;
  preview?: ReactNode;
  target: string;
  wrapperRef?: RefObject<HTMLAnchorElement> | RefObject<HTMLButtonElement>;
  collapsibleContent?: ReactNode;
  connectedRight: ReactNode;
  wrapperClassName?: string;
} & Partial<OnClickTarget> &
  Partial<URLTarget>;

const Wrapper = ({
  children,
  className,
  collapsibleContent,
  isOpen,
  url,
  onClick,
  isActive,
  onMouseEnter,
  onMouseLeave,
  preview,
  wrapperRef,
  target,
  connectedRight,
  wrapperClassName,
}: WrapperProps) => (
  <section
    className={twMerge(
      'group relative hover:rounded-lg hover:bg-gray-3',
      isActive && 'rounded-lg bg-gray-3',
      wrapperClassName
    )}
  >
    {collapsibleContent ? (
      <CollapsibleRoot open={isOpen}>
        <div className="text-sm text-gray-9 transition-all">
          <Trigger asChild>
            <button
              className={className}
              onClick={() => {
                onClick?.();
              }}
              onMouseLeave={onMouseLeave}
              onMouseEnter={onMouseEnter}
              ref={wrapperRef as RefObject<HTMLButtonElement>}
            >
              {children}
            </button>
          </Trigger>
          <Content>
            <div className="overflow-hidden pl-10 pr-4">
              {collapsibleContent}
            </div>
          </Content>
        </div>
      </CollapsibleRoot>
    ) : (
      <>
        {url ? (
          <Link
            to={url}
            className={className}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={() => onClick?.()}
            ref={wrapperRef as RefObject<HTMLAnchorElement>}
            target={target}
          >
            {children}
          </Link>
        ) : (
          <button
            className={className}
            onClick={() => {
              onClick?.();
            }}
            onMouseLeave={onMouseLeave}
            onMouseEnter={onMouseEnter}
            ref={wrapperRef as RefObject<HTMLButtonElement>}
          >
            {children}
          </button>
        )}
        {preview}
      </>
    )}
    {connectedRight && (
      <div
        role="presentation"
        onMouseLeave={onMouseLeave}
        onMouseEnter={onMouseEnter}
        className="absolute right-[12px] top-2 h-[32px] w-auto max-sm:invisible"
      >
        {connectedRight}
      </div>
    )}
  </section>
);

export function CardSkeletonLoader() {
  return (
    <ContentLoader
      speed={2}
      width="100%"
      height={100}
      backgroundColor="#f3f3f3"
      foregroundColor="#ecebeb"
    >
      <circle cx="26" cy="55" r="14" />
      <rect x="53" y="22" rx="4" ry="4" width="59" height="15" />
      <rect x="52" y="45" rx="4" ry="4" width="270" height="20" />
      <rect x="304" y="54" rx="4" ry="4" width="3" height="4" />
      <rect x="52" y="75" rx="4" ry="4" width="144" height="13" />
    </ContentLoader>
  );
}

export function Card(props: CardProps) {
  const {
    url,
    icon,
    text,
    subText,
    onClick,
    connectedRight,
    onToolbarItemClick,
    primaryToolbarItems,
    secondaryToolbarItems,
    preview,
    wrapperRef,
    detailsRef,
    onMenuToggle,
    collapsibleContent,
    color,
    target = '_self',
    wrapperClassName,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const handleOnClick = () => {
    if (collapsibleContent) {
      setIsOpen(!isOpen);
    }
    onClick?.();
  };

  return (
    <Wrapper
      url={url}
      onClick={handleOnClick}
      preview={preview}
      isActive={isActive}
      wrapperRef={wrapperRef}
      onMouseEnter={() => {
        setIsActive(true);
      }}
      onMouseLeave={(event: React.MouseEvent) => {
        if (
          wrapperRef?.current &&
          !checkCursorInsideElement(wrapperRef.current, {
            x: event.clientX,
            y: event.clientY,
          })
        ) {
          setIsActive(false);
        }
      }}
      isOpen={isOpen}
      collapsibleContent={collapsibleContent}
      className="block w-full text-left text-gray-9 focus:rounded-lg focus:bg-gray-3 max-md:px-2"
      wrapperClassName={wrapperClassName}
      target={target}
      connectedRight={connectedRight}
    >
      <div
        className="group grid w-full grid-cols-[max-content_minmax(0,1fr)_max-content_max-content] gap-x-3 px-3 py-2 text-left text-gray-9 focus:rounded-lg focus:bg-gray-3 max-md:px-2"
        ref={detailsRef}
      >
        <div className="col-start-1 row-start-1 flex flex-row items-center">
          {icon}
        </div>

        <div className="relative col-start-2 flex items-center pb-1">
          {color ? (
            <TextStyle
              variant="base-medium"
              className={classNames(
                'truncate rounded px-2',
                mapColorToCssClass(color)
              )}
              html={text}
            />
          ) : (
            <TextStyle className="truncate" html={text} />
          )}
          {collapsibleContent && (
            <ChevronDownIcon
              className={classNames('ml-2 inline h-4 w-4', {
                'rotate-180 transform': isOpen,
              })}
            />
          )}
        </div>

        {subText && (
          <TextStyle
            className="col-start-2 mt-[-4px] line-clamp-2"
            variant="sm-regular"
            html={subText}
            subdued
          />
        )}

        {isActive &&
          onToolbarItemClick &&
          secondaryToolbarItems &&
          secondaryToolbarItems.length > 0 && (
            <div
              className={classNames(
                'col-start-3 row-start-1 row-end-4 max-md:hidden'
              )}
            >
              <Toolbar
                hideToolbar={!isActive}
                onClosed={() => {
                  setIsActive(false);
                }}
                onMenuItemClick={(arg) => {
                  setIsActive(false);
                  onToolbarItemClick(arg);
                }}
                onToggle={onMenuToggle}
                primaryToolbarItems={primaryToolbarItems}
                secondaryToolbarItems={secondaryToolbarItems}
              />
            </div>
          )}
        {connectedRight && (
          <div className="col-start-4 row-start-1 row-end-4 justify-end max-sm:col-start-2 max-sm:row-start-auto max-sm:row-end-auto sm:invisible">
            {connectedRight}
          </div>
        )}
      </div>
    </Wrapper>
  );
}
